import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Stack, Typography } from "@mui/material";
import {
  ClientsService,
  ExecutionJob,
  ExecutionJobCollection,
  ExecutionJobsService,
  ExecutionJobStatus,
  ExecutionJobType,
  UserGroup
} from "gen/clients";
import { useUserSession } from "hooks/useUserSession";
import { useDialogState } from "hooks/useDialogState";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { formatDate } from "utils/dateUtils";

const QUERY_KEY = "listClientConfigSyncJobs";

const XtrfSyncStatus: React.FC = () => {
  const queryClient = useQueryClient();
  const { roles } = useUserSession();
  const [confirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useDialogState();

  const isSuperUser = React.useMemo(() => roles?.includes(UserGroup.SUPER_USER) || false, [roles]);

  const { data: executionJobs } = useQuery<ExecutionJobCollection>([QUERY_KEY], {
    queryFn: () =>
      ExecutionJobsService.listExecutionJobs({
        type: ExecutionJobType.CLIENT_CONFIG_SYNC,
        pageSize: 10
      }),
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: data => {
      const lastJob = data?.items?.[0];
      if (lastJob && (lastJob.status === ExecutionJobStatus.PENDING || lastJob.status === ExecutionJobStatus.RUNNING)) {
        return 5000;
      }
      return false;
    }
  });

  const {
    mutate: triggerSync,
    isLoading: isSyncLoading,
    error: syncError,
    reset: resetSyncError
  } = useMutation({
    mutationFn: ClientsService.syncAllClientConfigsWithXtrf,
    onSuccess: () => {
      closeConfirmDialog();
      queryClient.invalidateQueries([QUERY_KEY]);
    }
  });

  const onConfirmSync = React.useCallback(() => {
    triggerSync();
  }, [triggerSync]);

  const onCloseConfirmDialog = React.useCallback(() => {
    if (!isSyncLoading) {
      closeConfirmDialog();
      resetSyncError();
    }
  }, [isSyncLoading, closeConfirmDialog, resetSyncError]);

  const lastJob: ExecutionJob | undefined = executionJobs?.items?.[0];
  const lastSuccessfulJob: ExecutionJob | undefined = React.useMemo(
    () => executionJobs?.items?.find(job => job.status === ExecutionJobStatus.COMPLETED),
    [executionJobs]
  );

  const renderStatus = () => {
    if (!lastJob) {
      return null;
    }

    if (lastJob.status === ExecutionJobStatus.PENDING || lastJob.status === ExecutionJobStatus.RUNNING) {
      const percentage = lastJob.progress?.percentage;
      return (
        <Typography variant="body2" color="info.main" noWrap>
          Sync in progress{percentage !== undefined ? ` (${Math.round(percentage)}%)` : ""}...
        </Typography>
      );
    }

    if (lastJob.status === ExecutionJobStatus.COMPLETED) {
      return (
        <Typography variant="body2" color="success.main" noWrap>
          Last sync completed: {formatDate(lastJob.completedAt)}
        </Typography>
      );
    }

    if (lastJob.status === ExecutionJobStatus.FAILED) {
      return (
        <Stack spacing={0}>
          {lastSuccessfulJob && (
            <Typography variant="body2" color="success.main" noWrap>
              Last successful sync completed: {formatDate(lastSuccessfulJob.completedAt)}
            </Typography>
          )}
          <Typography variant="body2" color="error.main" noWrap>
            Sync failed at {formatDate(lastJob.completedAt || lastJob.updatedAt)}
            {lastJob.error?.message ? `: ${lastJob.error.message}` : ""}
          </Typography>
        </Stack>
      );
    }

    return null;
  };

  return (
    <>
      {syncError && <SnackbarApiError error={syncError} />}
      <Stack direction="row" spacing={2} alignItems="center">
        {renderStatus()}
        {isSuperUser && (
          <LoadingButton
            variant="outlined"
            color="primary"
            type="button"
            onClick={openConfirmDialog}
            isLoading={lastJob?.status === ExecutionJobStatus.PENDING || lastJob?.status === ExecutionJobStatus.RUNNING}
          >
            Sync All Clients
          </LoadingButton>
        )}
      </Stack>
      {confirmDialogOpen && (
        <ConfirmationDialog
          title="Sync All Clients"
          content="Are you sure you want to synchronize all clients with XTRF? This may take a few minutes."
          onClose={onCloseConfirmDialog}
          onConfirm={onConfirmSync}
          isLoading={isSyncLoading}
          error={syncError ? <ApiErrorMessage apiError={syncError} /> : undefined}
        />
      )}
    </>
  );
};

export { XtrfSyncStatus };
